/** Shared status / filter pill colors — use instead of inline hex in pages. */
export type StatusPillStyle = { accent: string; bg: string; border: string };

/** Token-based neutral for status chips (non-filter). */
export const STATUS_PILL_NEUTRAL: StatusPillStyle = {
  accent: 'var(--wp-feldspar-600)',
  bg: 'var(--wp-surface-elevated)',
  border: 'var(--wp-border-medium)',
};

/** Default « Tous » filter pill — lift on parchment, not grey dunk. */
export const FILTER_PILL_NEUTRAL: StatusPillStyle = {
  accent: 'var(--wp-feldspar-600)',
  bg: 'var(--wp-surface-elevated)',
  border: 'var(--wp-border-medium)',
};

/** Muted filter pill — archived, deleted, secondary states. */
export const FILTER_PILL_MUTED: StatusPillStyle = {
  accent: 'var(--wp-feldspar-500)',
  bg: 'color-mix(in oklab, var(--wp-feldspar-100) 35%, var(--wp-surface-elevated))',
  border: 'var(--wp-feldspar-300)',
};

export const FILTER_PILL_INFO: StatusPillStyle = {
  accent: 'var(--wp-info)',
  bg: 'color-mix(in srgb, var(--wp-info) 12%, var(--wp-surface-card))',
  border: 'color-mix(in srgb, var(--wp-info) 30%, transparent)',
};

export const FILTER_PILL_SUCCESS: StatusPillStyle = {
  accent: 'var(--wp-success-strong)',
  bg: 'var(--wp-success-surface)',
  border: 'var(--wp-success-border)',
};

export const FILTER_PILL_WARNING: StatusPillStyle = {
  accent: 'var(--wp-warning-strong)',
  bg: 'var(--wp-warning-surface)',
  border: 'var(--wp-warning-border)',
};

export const FILTER_PILL_DANGER: StatusPillStyle = {
  accent: 'var(--wp-danger-strong)',
  bg: 'var(--wp-danger-surface)',
  border: 'var(--wp-danger-border)',
};

export const LEAD_STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
  new: {
    bg: 'color-mix(in srgb, var(--wp-info) 12%, var(--wp-surface-card))',
    fg: 'var(--wp-info)',
  },
  contacted: { bg: 'var(--wp-warning-surface)', fg: 'var(--wp-warning-strong)' },
  qualified: {
    bg: 'color-mix(in srgb, var(--wp-menthol-500) 16%, var(--wp-surface-card))',
    fg: 'var(--wp-brunswick-700)',
  },
  converted: { bg: 'var(--wp-success-surface)', fg: 'var(--wp-success-strong)' },
  lost: { bg: 'var(--wp-feldspar-100)', fg: 'var(--wp-feldspar-600)' },
};

export const LIFECYCLE_INDICATOR_COLORS = {
  success: 'var(--wp-success-strong)',
  warning: 'var(--wp-warning-strong)',
  danger: 'var(--wp-danger-strong)',
  info: 'var(--wp-info)',
  muted: 'var(--wp-feldspar-500)',
} as const;

export const ACTIVITY_EVENT_COLORS: Record<string, string> = {
  STATUS_CHANGE: 'var(--wp-info)',
  NOTE: 'var(--wp-feldspar-500)',
  DEFAULT: 'var(--wp-feldspar-600)',
  FOLLOW_UP_SET: 'var(--wp-warning-strong)',
  CONVERTED: 'var(--wp-success-strong)',
  ASSIGNED: 'var(--wp-info)',
};

export const PRODUCT_TYPE_ACTIVE_DOT = {
  active: { fill: 'var(--wp-success-surface)', stroke: 'var(--wp-success-strong)' },
  inactive: { fill: 'var(--wp-danger-surface)', stroke: 'var(--wp-danger-strong)' },
} as const;

export const IMPORT_JOB_STATUS_UNKNOWN = {
  bg: FILTER_PILL_MUTED.bg,
  border: FILTER_PILL_MUTED.accent,
  label: '—',
  hint: 'Statut import.',
} as const;

export const IMPORT_STATUS_PULSE_RING = 'var(--wp-info)';

/** Import job status dot palette — shared with ImportJobStatusDot. */
export const IMPORT_JOB_STATUS_SPHERE: Record<
  string,
  { bg: string; border: string; pulse?: string; label: string; hint: string }
> = {
  PENDING: {
    bg: 'var(--wp-feldspar-100)',
    border: 'var(--wp-feldspar-500)',
    label: 'En file',
    hint: 'Import en attente de traitement.',
  },
  RUNNING: {
    bg: 'color-mix(in srgb, var(--wp-info) 12%, var(--wp-surface-card))',
    border: 'var(--wp-info)',
    pulse: IMPORT_STATUS_PULSE_RING,
    label: 'En cours',
    hint: 'Import en cours de traitement.',
  },
  COMPLETED: {
    bg: 'var(--wp-success-surface)',
    border: 'var(--wp-success-strong)',
    label: 'Terminé',
    hint: 'Import terminé.',
  },
  FAILED: {
    bg: 'var(--wp-danger-surface)',
    border: 'var(--wp-danger-strong)',
    label: 'Échec',
    hint: 'Import terminé avec erreur.',
  },
  CANCELLED: {
    bg: 'var(--wp-warning-surface)',
    border: 'var(--wp-warning-strong)',
    label: 'Annulé',
    hint: 'Import annulé.',
  },
};

export type ReadinessTone = 'GREEN' | 'AMBER' | 'RED' | 'LOCKED';

/** Catalog readiness / PO state dot fills — semantic surfaces + strong borders. */
export const READINESS_STAGE_DOT: Record<ReadinessTone, { bg: string; border: string }> = {
  GREEN: { bg: 'var(--wp-success-surface)', border: 'var(--wp-success-strong)' },
  AMBER: { bg: 'var(--wp-warning-surface)', border: 'var(--wp-warning-strong)' },
  RED: { bg: 'var(--wp-danger-surface)', border: 'var(--wp-danger-strong)' },
  LOCKED: { bg: 'var(--wp-surface-muted)', border: 'var(--wp-feldspar-400)' },
};

export function poToneDotColors(tone: 'red' | 'orange' | 'green'): { bg: string; border: string } {
  return tone === 'green'
    ? READINESS_STAGE_DOT.GREEN
    : tone === 'orange'
      ? READINESS_STAGE_DOT.AMBER
      : READINESS_STAGE_DOT.RED;
}

/** Batch lot traffic-light dots — maps DS status tone to pill colors. */
export function batchLotTrafficColors(tone: string): { bg: string; border: string } {
  switch (tone) {
    case 'success':
      return READINESS_STAGE_DOT.GREEN;
    case 'warning':
      return READINESS_STAGE_DOT.AMBER;
    case 'danger':
      return READINESS_STAGE_DOT.RED;
    default:
      return READINESS_STAGE_DOT.LOCKED;
  }
}

export const CUSTOMER_LIFECYCLE_PILL: Record<'pending' | 'active' | 'banned', StatusPillStyle> = {
  pending: {
    accent: 'var(--wp-warning-strong)',
    bg: 'var(--wp-warning-surface)',
    border: 'var(--wp-warning-strong)',
  },
  active: {
    accent: 'var(--wp-success-strong)',
    bg: 'var(--wp-success-surface)',
    border: 'var(--wp-success-strong)',
  },
  banned: {
    accent: 'var(--wp-danger-strong)',
    bg: 'var(--wp-danger-surface)',
    border: 'var(--wp-danger-strong)',
  },
};

export const LEAD_SOURCE_COLORS: Record<string, { bg: string; fg: string }> = {
  skinAnalyzer: {
    bg: 'color-mix(in srgb, var(--wp-info) 14%, var(--wp-surface-card))',
    fg: 'var(--wp-info)',
  },
  'skin-analyzer': {
    bg: 'color-mix(in srgb, var(--wp-menthol-400) 14%, var(--wp-surface-card))',
    fg: 'var(--wp-brunswick-600)',
  },
  regimenBuilder: {
    bg: 'color-mix(in srgb, var(--wp-feldspar-500) 14%, var(--wp-surface-card))',
    fg: 'var(--wp-feldspar-700)',
  },
};

export const LEAD_SOURCE_DEFAULT: { bg: string; fg: string } = {
  bg: 'var(--wp-surface-muted)',
  fg: 'var(--wp-text-muted)',
};
